import type { GlobalFilters } from '@/domains/filters/filter.entity';
import type {
  FilterSelection,
  FilteredStudentsResult,
  StudentResponseEntity,
  UIFilters,
} from '@/domains/students/student.entity';
import {
  StudentFilterDomain,
  type StudentFilters,
} from '@/domains/students/studentFilter.domain';
import { RISK_LEVEL, JORNADA_OPTIONS } from '@/shared/constants';

/**
 * Use Case para filtrado complejo de estudiantes
 * Cumple SRP: Solo se encarga de aplicar lógica de filtrado
 * Extrae lógica de negocio que estaba en hooks UI
 *
 * Responsabilidad: Transformar filtros UI en filtros de dominio y aplicarlos
 */
export class FilterStudentsUseCase {
  /**
   * Transforma FilterSelection de UI en array de strings para filtros.
   * Optimiza el filtrado devolviendo undefined cuando todas las opciones están seleccionadas.
   *
   * @param selection - Selección del filtro UI ('all' o Set<string>)
   * @param totalOptionsLength - Número total de opciones disponibles en el filtro
   * @returns Array de strings con los valores seleccionados, o undefined si todas las opciones están activas
   *
   * @example
   * // Cuando todas están seleccionadas
   * transformSelectionToFilter('all', 5) // undefined
   *
   * // Cuando algunas están seleccionadas
   * transformSelectionToFilter(new Set(['ALTO', 'MEDIO']), 3) // ['ALTO', 'MEDIO']
   */
  private transformSelectionToFilter(
    selection: FilterSelection,
    totalOptionsLength: number,
  ): string[] | undefined {
    if (
      selection === 'all' ||
      (selection instanceof Set &&
        Array.from(selection).length === totalOptionsLength)
    ) {
      return undefined;
    }
    return selection instanceof Set
      ? (Array.from(selection) as string[])
      : undefined;
  }

  /**
   * Transforma filtros UI en filtros de dominio.
   * Extrae la lógica compleja que estaba en useStudent.hook.
   * Convierte estructuras de UI (Sets, 'all') en formato esperado por el dominio.
   *
   * @param uiFilters - Objeto con todos los filtros provenientes de la UI
   * @param uiFilters.activeFilter - Indicador de riesgo activo (Inasistencias, Notas, etc.)
   * @param uiFilters.searchQuery - Texto de búsqueda por RUT o nombre
   * @param uiFilters.statusFilter - Filtro de nivel de riesgo (ALTO, MEDIO, BAJO)
   * @param uiFilters.careerFilter - Filtro de carreras seleccionadas
   * @param uiFilters.careerOptions - Opciones disponibles de carreras
   *
   * @returns Objeto con filtros transformados listos para ser usados por StudentFilterDomain
   *
   * @example
   * const uiFilters = {
   *   activeFilter: 'Inasistencias',
   *   searchQuery: '12345678',
   *   statusFilter: new Set(['ALTO', 'MEDIO']),
   *   careerFilter: 'all',
   *   careerOptions: ['ING', 'MED']
   * };
   * const domainFilters = transformUIFilters(uiFilters);
   * // { indicadorRiesgo: 'Inasistencias', searchQuery: '12345678', risk_level: ['ALTO', 'MEDIO'], career_short_name: undefined }
   */
  transformUIFilters(
    uiFilters: UIFilters,
  ): Omit<StudentFilters, 'year' | 'semester'> {
    const {
      activeFilter,
      searchQuery,
      statusFilter,
      careerFilter,
      jornadaFilter,
      careerOptions = [],
    } = uiFilters;

    const riskLevelFilter = statusFilter
      ? this.transformSelectionToFilter(statusFilter, RISK_LEVEL.length)
      : undefined;

    const careerFilterValue = careerFilter
      ? this.transformSelectionToFilter(careerFilter, careerOptions.length)
      : undefined;

    const scheduleFilter = jornadaFilter
      ? this.transformSelectionToFilter(jornadaFilter, JORNADA_OPTIONS.length)
      : undefined;

    return {
      indicadorRiesgo: activeFilter || undefined,
      searchQuery: searchQuery || undefined,
      risk_level: riskLevelFilter,
      career_short_name: careerFilterValue,
      schedule: scheduleFilter,
    };
  }

  /**
   * Combina filtros globales (año/semestre) con filtros UI transformados.
   * Crea el objeto completo de StudentFilters necesario para el dominio.
   *
   * @param globalFilters - Filtros globales de la aplicación (año y semestre)
   * @param globalFilters.year - Año académico seleccionado
   * @param globalFilters.semester - Semestre seleccionado (1 o 2)
   * @param uiFilters - Filtros específicos de la UI de estudiantes
   *
   * @returns Objeto StudentFilters completo con filtros globales y UI combinados
   */
  private combineFilters(
    globalFilters: GlobalFilters,
    uiFilters: UIFilters,
  ): StudentFilters {
    const transformedUIFilters = this.transformUIFilters(uiFilters);

    return {
      year: globalFilters.year,
      semester: globalFilters.semester,
      ...transformedUIFilters,
    };
  }

  /**
   * Aplica filtros a lista de estudiantes usando dominio encapsulado.
   * Extrae la lógica que estaba en useStudentLocalFiltering.
   * Valida entrada y retorna información detallada del filtrado.
   *
   * @param students - Array de estudiantes a filtrar
   * @param globalFilters - Filtros globales (año/semestre)
   * @param uiFilters - Filtros de la interfaz de usuario
   *
   * @returns Objeto con estudiantes filtrados, información de filtros activos y contadores
   * @returns {FilteredStudentsResult} Resultado con:
   *   - filteredStudents: Array de estudiantes después de aplicar filtros
   *   - filterInfo: Información sobre filtros activos (hasActiveFilters, activeFiltersCount, filterTypes)
   *   - totalCount: Total de estudiantes antes de filtrar
   *   - filteredCount: Total de estudiantes después de filtrar
   *
   * @example
   * const result = execute(students, { year: 2024, semester: 1 }, uiFilters);
   * console.log(`${result.filteredCount} de ${result.totalCount} estudiantes`);
   */
  execute(
    students: StudentResponseEntity[],
    globalFilters: GlobalFilters,
    uiFilters: UIFilters,
  ): FilteredStudentsResult {
    if (!students || students.length === 0) {
      return {
        filteredStudents: [],
        filterInfo: {
          hasActiveFilters: false,
          activeFiltersCount: 0,
          filterTypes: [],
        },
        totalCount: 0,
        filteredCount: 0,
      };
    }

    const combinedFilters = this.combineFilters(globalFilters, uiFilters);

    const filterDomain = new StudentFilterDomain(combinedFilters, false); // para aplicar filtros por año y semestre
    const activeFiltersInfo = filterDomain.getActiveFiltersInfo();

    const filteredStudents = activeFiltersInfo.hasActiveFilters
      ? filterDomain.applyTo(students)
      : students;

    return {
      filteredStudents,
      filterInfo: activeFiltersInfo,
      totalCount: students.length,
      filteredCount: filteredStudents.length,
    };
  }

  /**
   * Verifica si los filtros UI tienen algún valor activo.
   * Útil para mostrar indicadores visuales o limpiar filtros.
   *
   * @param uiFilters - Objeto con filtros de la UI a verificar
   * @param uiFilters.activeFilter - Indicador de riesgo activo
   * @param uiFilters.searchQuery - Texto de búsqueda
   * @param uiFilters.statusFilter - Filtro de nivel de riesgo
   * @param uiFilters.careerFilter - Filtro de carreras
   *
   * @returns true si hay al menos un filtro UI activo, false si todos están vacíos
   *
   * @example
   * const hasFilters = hasActiveUIFilters(uiFilters);
   * if (hasFilters) {
   *   // Mostrar botón "Limpiar filtros"
   * }
   */
  hasActiveUIFilters(uiFilters: UIFilters): boolean {
    const { activeFilter, searchQuery, statusFilter, careerFilter, jornadaFilter } =
      uiFilters;

    return !!(
      activeFilter ||
      searchQuery ||
      (statusFilter &&
        statusFilter !== 'all' &&
        !(statusFilter instanceof Set && statusFilter.size === 0)) ||
      (careerFilter &&
        careerFilter !== 'all' &&
        !(careerFilter instanceof Set && careerFilter.size === 0)) ||
      (jornadaFilter &&
        jornadaFilter !== 'all' &&
        !(jornadaFilter instanceof Set && jornadaFilter.size === 0))
    );
  }
}
